import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, Check, Printer } from 'lucide-react'

import type { TeaPurchase } from '@/lib/api/tea-purchase-api'
import { PageBackButton } from '@/components/common/PageBackButton'
import { TeaPurchasePrintDialog } from '@/components/tea-purchases/tea-purchase-print-dialog'
import {
    TeaPurchaseStatusBadge,
    teaPurchaseStatus,
} from '@/components/tea-purchases/tea-purchase-status-badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuth } from '@/hooks/use-auth'
import {
    useApproveTeaPurchase,
    useTeaPurchase,
} from '@/hooks/use-tea-purchase-query'
import { formatQty, formatVN } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import { areaNames, patternLabel } from '@/lib/tea-purchase-labels'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/tea-purchases/$id',
)({
    component: TeaPurchaseDetailPage,
})

function TeaPurchaseDetailPage() {
    const { id } = Route.useParams()
    const { hasPermission } = useAuth()
    const canApprove = hasPermission('tea_purchases.approve')

    const { data, isLoading } = useTeaPurchase(id)
    const approve = useApproveTeaPurchase()
    const [printOpen, setPrintOpen] = useState(false)

    if (isLoading || !data) {
        return (
            <div className="p-6 text-muted-foreground">
                {m.common_loading()}
            </div>
        )
    }

    const t: TeaPurchase = data
    const status = teaPurchaseStatus(t)

    const handleApprove = () => {
        if (window.confirm(m.tea_purchase_approve_confirm())) {
            approve.mutate(t.id)
        }
    }

    return (
        <div className="space-y-4 page-pad">
            <div className="flex items-center justify-end gap-2">
                <div className="flex items-center gap-2">
                    {status === 'pending' && canApprove && (
                        <Button
                            size="sm"
                            onClick={handleApprove}
                            disabled={approve.isPending}
                        >
                            <Check className="size-4 mr-1" />
                            {m.tea_purchase_action_approve()}
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPrintOpen(true)}
                    >
                        <Printer className="size-4 mr-1" />
                        {m.tea_purchase_print_fresh()}
                    </Button>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <PageBackButton asChild>
                    <Link to="/tea-purchases">
                        <ArrowLeft className="size-4" />
                    </Link>
                </PageBackButton>
                <h1 className="text-base md:text-2xl font-semibold font-mono">
                    {t.code}
                </h1>
                <TeaPurchaseStatusBadge teaPurchase={t} />
                {t.approved_at && (
                    <span className="text-xs text-muted-foreground">
                        {m.tea_purchase_approved_at_by({
                            time: formatVN(t.approved_at),
                            name: t.approved_by_name || '',
                        })}
                    </span>
                )}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
                <Card>
                    <CardHeader>
                        <CardTitle>{m.tea_purchase_title()}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                        <Row
                            label={m.tea_purchase_grower()}
                            value={t.grower_name || '—'}
                        />
                        <Row
                            label={m.tea_purchase_tea_type()}
                            value={t.tea_type_name || '—'}
                        />
                        <Row
                            label={m.tea_purchase_areas()}
                            value={areaNames(t.areas)}
                        />
                        <Row
                            label={m.tea_purchase_quantity()}
                            value={`${formatQty(t.quantity)} kg`}
                        />
                        <Row
                            label={m.tea_purchase_pattern()}
                            value={
                                t.tea_pattern
                                    ? patternLabel[t.tea_pattern]()
                                    : '—'
                            }
                        />
                        <Row
                            label={m.tea_purchase_purchase_date()}
                            value={formatVN(t.purchase_date)}
                        />
                        <Row
                            label={m.tea_purchase_note()}
                            value={t.note || '—'}
                        />
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{m.dtp_title()}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                        {t.dried_tea_purchase_id ? (
                            <div className="space-y-1">
                                <p className="text-muted-foreground">
                                    {m.dtp_merged_into()}
                                </p>
                                <Link
                                    to="/dried-tea-purchases/$id"
                                    params={{ id: t.dried_tea_purchase_id }}
                                    className="text-primary hover:underline font-mono"
                                >
                                    {t.dried_tea_purchase_code || '—'}
                                </Link>
                            </div>
                        ) : (
                            <p className="text-muted-foreground">
                                {m.tea_purchase_not_processed()}
                            </p>
                        )}
                        {status === 'pending' && (
                            <p className="text-xs text-amber-600">
                                {m.tea_purchase_need_approve_hint()}
                            </p>
                        )}
                    </CardContent>
                </Card>
            </div>

            <TeaPurchasePrintDialog
                open={printOpen}
                onOpenChange={(v) => !v && setPrintOpen(false)}
                mode="fresh"
                teaPurchase={t}
                entry="fresh_detail"
            />
        </div>
    )
}

function Row({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex">
            <span className="w-1/3 text-muted-foreground">{label}</span>
            <span className="flex-1">{value}</span>
        </div>
    )
}
